import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { CircularProgress, Grid } from '@mui/material';

import * as amplitude from '@amplitude/analytics-browser';
import { jwtDecode } from 'jwt-decode';

import { useAppState } from '../contexts/AppStateProvider';
// Use new hooks instead of legacy context
import { useJWT } from '../contexts/JWTProvider';
import { LinkToken } from '../types/LinkToken';
import { getNavigationAction } from '../utils/jwtHelpers';
import { safeRedirectReplace } from '../utils/urlUtils';

export default function Confirmation() {
  // Use new centralized hooks
  const jwt = useJWT();
  const navigate = useNavigate();
  const { TSP, onboarded, redirectURI, serviceName } = useAppState();

  const decodedToken = jwtDecode<LinkToken>(jwt.rawToken || '');
  amplitude.setUserId('service-' + String(decodedToken.sid));
  amplitude.setDeviceId('fleet-code-' + decodedToken.fc);

  const OnboardedDiv = () => (
    <div>{TSP} account linked! You may now close this page.</div>
  );

  const NotOnboardedDiv = () => {
    useEffect(() => {
      amplitude.track('Error Linking', {
        tsp: TSP,
        serviceName: serviceName,
      });

      // Use new navigation action system instead of legacy showTryAgain/showSkip
      const navigationAction = getNavigationAction(jwt);

      // Navigate to proper error page with new navigation system
      navigate('/error', {
        state: {
          errorMsg: `Error occurred linking ${TSP}.`,
          errorCaption:
            'Please try to onboard again or contact support for assistance.',
          errorType: 'onboard_error',
          navigationAction: navigationAction, // Use new system
        },
      });
      // ESLint disable: navigate is stable from React Router and doesn't need to be in deps
      // TSP, serviceName, and jwt are used in the effect and should remain as dependencies
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [TSP, serviceName, jwt]);

    return <div>Redirecting to error page...</div>;
  };

  function RedirectingDiv() {
    useEffect(() => {
      setTimeout(() => {
        safeRedirectReplace(redirectURI);
      }, 2000);
    }, []);

    return (
      <>
        <div>{TSP} account linked! Redirecting...</div>
        <CircularProgress />
      </>
    );
  }

  const CheckDiv = () => {
    useEffect(() => {
      amplitude.track('Successfully Linked', {
        tsp: TSP,
        serviceName: serviceName,
      });
    }, []);
    return <>{redirectURI ? <RedirectingDiv /> : <OnboardedDiv />}</>;
  };

  return (
    <Grid
      container
      direction='column'
      justifyContent='center'
      alignItems='center'
    >
      {onboarded ? <CheckDiv /> : <NotOnboardedDiv />}
    </Grid>
  );
}
